import PrintButton from "../../../../../components/Reports/Buttons/PrintButton";
import ReportHeader from "../../../../../components/Reports/ReportHeader";
import { ReportPageProps } from "../../../../../types";
import { ChartStyles } from "../../Styles/QuarterlyStyles";
import styled from "styled-components/macro";
import {
  QiagenDataByMachineType,
  QiagenQuarterlyAllSkuMachineDataType,
  QiagenQuarterlyDataType,
  sumQiagenQuarterlyData,
} from "../../utils/QiagenQuarterlyUtils";
import { darkBlue, lightishGrey } from "../../../../../constants/colors";
import { ceil, round } from "lodash";

const QiagenQuarterlyOverviewStyles = styled.div`
  padding: 0 5%;
  font-size: 11px;

  .row {
    display: grid;
    grid-template-rows: 1fr;
    grid-template-columns: 3fr 1fr 1fr 1fr 2fr;
  }
  .description {
    background: ${lightishGrey};
    border: 3px solid ${lightishGrey};
    padding: 15px 25px;
    font-size: 13px;
    font-weight: bold;
  }
  .machineBody {
    border-left: 3px solid ${lightishGrey};
    border-right: 3px solid ${lightishGrey};
    border-bottom: 3px solid ${lightishGrey};
  }
  .lastSku {
    padding-bottom: 10px;
    padding-left: 25px;
    padding-right: 25px;
  }
  .machine {
    font-weight: bold;
    padding: 10px 25px;
  }
  .sku {
    padding: 0 25px;
  }
  .overall {
    font-weight: bold;
    color: white;
    padding: 15px 25px;
    font-size: 13px;
    background: ${darkBlue};
  }
`;

const ColumnContent = styled.div`
  font-family: "Gotham";
  font-style: normal;
  margin-right: 5px;
`;

type QiQuarterlyRowProps = {
  name: string;
  expectedTargetProduced: number;
  percentYield: string;
  sumOfDuration: number;
  totalUnitsProduced: number;
  rowType: string;
};

const QuarterlyOverviewRow = ({
  name,
  expectedTargetProduced,
  percentYield,
  sumOfDuration,
  totalUnitsProduced,
  rowType,
}: QiQuarterlyRowProps) => {
  return (
    // rounding done for bug -> where 0.8 + 0.1 = 0.90000001 for some reason
    <div key={name} className={`row ${rowType}`}>
      <ColumnContent>{name}</ColumnContent>
      <ColumnContent>{round(sumOfDuration, 1)}</ColumnContent>
      <ColumnContent>{expectedTargetProduced}</ColumnContent>
      <ColumnContent>{totalUnitsProduced}</ColumnContent>
      <ColumnContent>{percentYield}</ColumnContent>
    </div>
  );
};

const QuarterlyOverviewHeaderRow = () => (
  <div className="row description ">
    <ColumnContent>Row Labels (Sku or Machine)</ColumnContent>
    <ColumnContent>Sum of duration (in hours)</ColumnContent>
    <ColumnContent>Expected Target Product</ColumnContent>
    <ColumnContent>Total Production Achieved</ColumnContent>
    <ColumnContent>Percentage Yield</ColumnContent>
  </div>
);

const MAX_MACHINES_PER_PAGE = 10;

export default function QuarterlyOverviewSheet({
  subHeader,
  headerTitle,
  dateString,
  title,
  data,
}: ReportPageProps<QiagenDataByMachineType>) {
  const { finalDataByMachine, finalAggregateDataByMachine } = data || {
    finalDataByMachine: [] as QiagenQuarterlyAllSkuMachineDataType[],
    finalAggregateDataByMachine: [] as QiagenQuarterlyDataType[],
  };

  // we need to get out final row ourselves
  const overallData = sumQiagenQuarterlyData(
    finalAggregateDataByMachine,
    "Grand Total"
  );

  const QuarterlyOverviewOverallDataRow = () => (
    <QuarterlyOverviewRow
      {...overallData}
      rowType={"overall"}
      name={overallData.skuNameStatic}
    />
  );

  const tooManyMachinesForOnePage =
    finalDataByMachine.length > MAX_MACHINES_PER_PAGE;

  const totalPagesForChart = tooManyMachinesForOnePage
    ? ceil(finalDataByMachine.length / MAX_MACHINES_PER_PAGE)
    : 1;

  const pagesArray = [...new Array(totalPagesForChart)];
  return (
    <>
      {pagesArray.map((_, pageIndex) => (
        <ChartStyles key={`${headerTitle}-${pageIndex}`}>
          <ReportHeader
            title={headerTitle}
            dateString={dateString}
            rightSideComponent={<PrintButton />}
            subheader={subHeader}
          />
          <h1 className="chartTitle" key={title + ": actualTitle"}>
            {title}
          </h1>

          <QiagenQuarterlyOverviewStyles>
            <QuarterlyOverviewHeaderRow />
            {finalDataByMachine
              // we will slice our data in pages of 10 page1: {0, 10} page 2: {11: 20} etc..
              ?.slice(
                MAX_MACHINES_PER_PAGE * pageIndex,
                MAX_MACHINES_PER_PAGE * (pageIndex + 1)
              )
              .map(({ machineName, skuDataOrganizedByMachine }, index) => {
                const machineTotalData = finalAggregateDataByMachine
                  ? finalAggregateDataByMachine[index]
                  : ({} as QiagenQuarterlyDataType);

                const QuarterlyOverviewMachineOverallRow = () => (
                  <QuarterlyOverviewRow
                    name={machineName}
                    {...machineTotalData}
                    rowType={"machine"}
                  />
                );

                return (
                  <div key={machineName} className="machineBody">
                    <QuarterlyOverviewMachineOverallRow />
                    {skuDataOrganizedByMachine.map(
                      (
                        {
                          skuNameStatic,
                          expectedTargetProduced,
                          percentYield,
                          sumOfDuration,
                          totalUnitsProduced,
                        },
                        skuIndex
                      ) => {
                        const lastMachine =
                          skuDataOrganizedByMachine.length - 1 === skuIndex;

                        return machineName !== skuNameStatic ? (
                          <QuarterlyOverviewRow
                            name={skuNameStatic}
                            expectedTargetProduced={expectedTargetProduced}
                            percentYield={percentYield}
                            sumOfDuration={sumOfDuration}
                            totalUnitsProduced={totalUnitsProduced}
                            rowType={`${lastMachine ? "lastSku" : "sku"}`}
                          />
                        ) : null;
                      }
                    )}
                  </div>
                );
              })}
            {pageIndex + 1 === totalPagesForChart && (
              <QuarterlyOverviewOverallDataRow />
            )}
          </QiagenQuarterlyOverviewStyles>
        </ChartStyles>
      ))}
    </>
  );
}
